<template>
    <AdminLayout :crumbs="crumbs" :season_id="season_id">
        <template v-if="season">
            <EventHeader :season="season">
                <template #actions>
                    <button v-if="is_admin" type="button" class="btn btn-primary" @click="open_form(null)"><i class="bi bi-plus-lg"></i>Nueva etapa</button>
                </template>
            </EventHeader>

            <div class="a-card">
                <draggable v-if="stages_list.length" v-model="stages_list" item-key="id" tag="ul" class="a-list"
                    handle=".a-handle" ghostClass="a-ghost" :disabled="!is_admin" @end="persist_order">
                    <template #item="{ element: stage, index }">
                        <li class="a-row a-row-link" @click="open_rundown(stage)">
                            <i v-if="is_admin" class="bi bi-grip-vertical a-handle" @click.stop title="Arrastrar para reordenar"></i>
                            <div class="stage-num">{{ index + 1 }}</div>
                            <div class="a-row-main">
                                <div class="d-flex align-items-center gap-2">
                                    <span class="a-row-title">{{ stage.name }}</span>
                                    <span class="a-pill" :class="status(stage).cls">{{ status(stage).label }}</span>
                                </div>
                                <div class="a-row-meta">
                                    <span><i class="bi bi-calendar3"></i>{{ range(stage) }}</span>
                                    <span><i class="bi bi-list-ol"></i>{{ plural(count_activities(stage), 'actividad', 'actividades') }}</span>
                                    <span v-if="count_stops(stage)"><i class="bi bi-signpost-split"></i>{{ plural(count_stops(stage), 'parada') }}</span>
                                    <span v-if="is_playing(stage)" class="text-danger"><i class="bi bi-broadcast text-danger"></i>Actividad {{ playback.index + 1 }} de {{ playback.question_ids.length }}</span>
                                </div>
                            </div>
                            <div class="a-row-actions" @click.stop v-if="is_admin">
                                <button v-if="is_playing(stage)" type="button" class="btn btn-secondary btn-sm" @click="open_rundown(stage)"><i class="bi bi-broadcast"></i>Ver en directo</button>
                                <button v-else-if="!playback && !stage.ended_at" type="button" class="btn btn-secondary btn-sm" :disabled="!count_activities(stage)" @click="send('START_STAGE_PLAYBACK', stage.id)"><i class="bi bi-play-fill"></i>Iniciar</button>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-ghost btn-icon" title="Más acciones" data-bs-toggle="dropdown" aria-expanded="false"><i class="bi bi-three-dots"></i></button>
                                    <ul class="dropdown-menu dropdown-menu-end">
                                        <li><a class="dropdown-item" href="javascript:void(0)" @click="open_form(stage)"><i class="bi bi-pencil"></i>Editar</a></li>
                                        <li v-if="!stage.started_at"><a class="dropdown-item" href="javascript:void(0)" @click="send('START_STAGE', stage.id)"><i class="bi bi-unlock"></i>Marcar como abierta</a></li>
                                        <li v-if="stage.started_at && !stage.ended_at"><a class="dropdown-item" href="javascript:void(0)" @click="send('END_STAGE', stage.id)"><i class="bi bi-lock"></i>Marcar como finalizada</a></li>
                                        <li><a class="dropdown-item" href="javascript:void(0)" @click="ask_restart(stage)"><i class="bi bi-arrow-counterclockwise"></i>Reiniciar…</a></li>
                                        <li><hr class="dropdown-divider"></li>
                                        <li><a class="dropdown-item text-danger" href="javascript:void(0)" @click="ask_delete(stage)"><i class="bi bi-trash3"></i>Eliminar</a></li>
                                    </ul>
                                </div>
                                <i class="bi bi-chevron-right a-muted"></i>
                            </div>
                        </li>
                    </template>
                </draggable>
                <div v-else class="a-empty">
                    <i class="bi bi-signpost-split"></i>
                    <div class="a-empty-title">Este {{ season_label.toLowerCase() }} todavía no tiene etapas</div>
                    <div>Una etapa es una sesión de juego: una serie de actividades con sus paradas.</div>
                    <button v-if="is_admin" type="button" class="btn btn-primary mt-3" @click="open_form(null)"><i class="bi bi-plus-lg"></i>Crear la primera etapa</button>
                </div>
            </div>
            <p class="a-subtle mt-2" v-if="stages_list.length > 1 && is_admin">Arrastra <i class="bi bi-grip-vertical"></i> para cambiar el orden.</p>
        </template>
        <div v-else class="a-card a-empty">
            <i class="bi bi-question-circle"></i>
            <div class="a-empty-title">No se ha encontrado este {{ season_label.toLowerCase() }}</div>
            <router-link :to="{ name: 'Panel' }" class="btn btn-secondary mt-2">Volver al listado</router-link>
        </div>

        <!-- New / edit stage -->
        <div class="modal fade a-modal" ref="formModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content" v-if="draft">
                    <div class="modal-header">
                        <h1 class="modal-title">{{ draft.id ? 'Editar etapa' : 'Nueva etapa' }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <div class="mb-3">
                            <label class="form-label" for="stage-name">Nombre</label>
                            <input id="stage-name" type="text" class="form-control" v-model="draft.name" placeholder="Ej. Etapa 3 · Oviedo - Angliru">
                        </div>
                        <div class="row g-3">
                            <div class="col-6">
                                <label class="form-label" for="stage-start">Empieza</label>
                                <input id="stage-start" type="datetime-local" class="form-control" v-model="draft.start_date">
                            </div>
                            <div class="col-6">
                                <label class="form-label" for="stage-end">Termina</label>
                                <input id="stage-end" type="datetime-local" class="form-control" v-model="draft.end_date">
                            </div>
                            <div class="col-12 form-text mt-1" v-if="season.start_date || season.end_date">Tiene que estar dentro de las fechas del {{ season_label.toLowerCase() }}: {{ range(season) }}.</div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-primary" @click="save">{{ draft.id ? 'Guardar' : 'Crear etapa' }}</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Confirmations -->
        <div class="modal fade a-modal" ref="confirmModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content" v-if="confirm">
                    <div class="modal-header">
                        <h1 class="modal-title">{{ confirm.title }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">{{ confirm.text }}</div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-danger" @click="confirm.run(); confirm_modal.hide()">{{ confirm.action }}</button>
                    </div>
                </div>
            </div>
        </div>
    </AdminLayout>
</template>

<script>
import { Modal } from 'bootstrap'
import draggable from 'vuedraggable'
import utils from '@/../../shared/utils'
import AdminLayout from '@/components/admin/AdminLayout.vue'
import EventHeader from '@/components/admin/EventHeader.vue'
import { stage_status, format_range, to_datetime_local, plural, with_iso_dates } from '@/utils/admin-format'

// An event's stages, in play order. Opening one goes to its rundown.
export default {
    name: 'EventStages',
    components: { AdminLayout, EventHeader, draggable },
    data(){
        return { stages_list: [], draft: null, confirm: null };
    },
    computed: {
        season_id(){ return parseInt(this.$route.params.season_id, 10); },
        season(){ return (this.$store.state.game.seasons || []).find((s) => s.id == this.season_id) || null; },
        season_label(){ return utils.get_season_label(window.MODE); },
        crumbs(){
            return [
                { label: utils.get_season_label(window.MODE, true), to: { name: 'Panel' } },
                { label: this.season ? this.season.name : '…' },
            ];
        },
        is_admin(){
            const user = this.$store.state.game.user;
            return !!user && user.role == utils.USER_ROLES.ADMINISTRATOR;
        },
        playback(){ return this.$store.state.game.stage_playback; },
    },
    watch: {
        '$store.state.game.stages'(){ this.sync(); },
        season_id(){ this.sync(); },
    },
    mounted(){
        this.sync();
        this.form_modal = new Modal(this.$refs.formModal);
        this.confirm_modal = new Modal(this.$refs.confirmModal);
    },
    beforeUnmount(){
        this.form_modal.dispose();
        this.confirm_modal.dispose();
    },
    methods: {
        // <draggable> needs a plain mutable array, not a computed.
        sync(){
            this.stages_list = (this.$store.state.game.stages || [])
                .filter((st) => st.season_id == this.season_id)
                .slice()
                .sort((a, b) => a.order - b.order);
        },
        persist_order(){
            this.send('REORDER_STAGES', { season_id: this.season_id, order: this.stages_list.map((st) => st.id) });
        },
        plural,
        status(stage){ return stage_status(stage, this.playback); },
        range(item){ return format_range(item.start_date, item.end_date); },
        is_playing(stage){ return !!(this.playback && this.playback.stage_id == stage.id); },
        count_activities(stage){
            return (this.$store.state.game.questions || []).filter((q) => q.question && q.question.stage_id == stage.id).length;
        },
        count_stops(stage){
            return (this.$store.state.game.sectors || []).filter((s) => s.stage_id == stage.id && !s.is_default).length;
        },
        open_rundown(stage){
            this.$router.push({ name: 'StageRundown', params: { season_id: this.season_id, stage_id: stage.id } });
        },
        open_form(stage){
            this.draft = stage
                ? { id: stage.id, season_id: stage.season_id, name: stage.name, start_date: to_datetime_local(stage.start_date), end_date: to_datetime_local(stage.end_date) }
                : { season_id: this.season_id, name: '', start_date: null, end_date: null };
            this.form_modal.show();
        },
        save(){
            if(!utils.is_valid_parameter(this.draft.name)){
                window.toast.error('Ponle un nombre a la etapa.');
                return;
            }
            if(this.draft.start_date && this.draft.end_date && new Date(this.draft.start_date) > new Date(this.draft.end_date)){
                window.toast.error('La etapa no puede empezar después de terminar.');
                return;
            }
            this.send(this.draft.id ? 'EDIT_STAGE' : 'REGISTER_STAGE', with_iso_dates(this.draft, ['start_date', 'end_date']));
            this.form_modal.hide();
        },
        ask_restart(stage){
            this.confirm = {
                title: 'Reiniciar ' + stage.name,
                text: 'Se borran las respuestas dadas en esta etapa y se vuelve a lanzar desde el principio. No se puede deshacer.',
                action: 'Reiniciar etapa',
                run: () => this.send('RESTART_STAGE', stage.id),
            };
            this.confirm_modal.show();
        },
        ask_delete(stage){
            this.confirm = {
                title: 'Eliminar ' + stage.name,
                text: 'Se elimina la etapa con sus paradas. Sus actividades dejarán de estar accesibles desde el panel. No se puede deshacer.',
                action: 'Eliminar',
                run: () => this.send('REMOVE_STAGE', stage.id),
            };
            this.confirm_modal.show();
        },
        send(rpc, params){
            window.network.wso.send(JSON.stringify({ action: window.RPC[rpc], params }));
        }
    }
}
</script>

<style scoped>
.stage-num{
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--a-surface-2);
    border: 1px solid var(--a-line);
    color: var(--a-text-2);
    font-weight: 700;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
</style>
